import { Button } from '@/components/primitives/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/primitives/Dialog';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { type FC, type ReactNode, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ConfirmModalProps {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  children?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  cancelLabel?: TransTextKeys;
  confirmLabel?: TransTextKeys;
  interactOutside?: boolean;
  onCancel?: () => Promise<void> | void;
  onConfirm?: () => Promise<void> | void;
  isConfirmLoading?: boolean;
  showClose?: boolean;
  confirmVariant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'ghost'
    | 'ghostLink'
    | 'icon'
    | 'cta';
}

const ConfirmModal: FC<ConfirmModalProps> = ({
  open: controlledOpen,
  setOpen: controlledSetOpen,
  children,
  title,
  description,
  cancelLabel,
  confirmLabel,
  interactOutside = false,
  onCancel,
  onConfirm,
  showClose = true,
  isConfirmLoading,
  confirmVariant = 'primary',
}) => {
  const [unControlledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? unControlledOpen;
  const setOpen = controlledSetOpen ?? setUncontrolledOpen;
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const handleCancel = async () => {
    await onCancel?.();
    setOpen(false);
  };

  const handleConfirm = async () => {
    await onConfirm?.();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className="w-5/6 tablet:w-[480px]"
        onInteractOutside={
          interactOutside ? undefined : (event) => event.preventDefault()
        }
        onEscapeKeyDown={
          interactOutside ? undefined : (event) => event.preventDefault()
        }
      >
        <DialogHeader
          className="border-none p-4"
          showClose={showClose && !isConfirmLoading}
        >
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-dark" asChild>
          <div className="p-4">{description}</div>
        </DialogDescription>
        <DialogFooter className="border-none">
          {cancelLabel && (
            <Button
              data-testid="confirm-modal-cancel"
              variant="tertiary"
              className="rounded-lg laptop:w-auto"
              onClick={handleCancel}
              size={isLaptopOrBigger ? 'regular' : 'large'}
              fullWidth
            >
              <TransText i18nKey={cancelLabel} />
            </Button>
          )}
          {confirmLabel && (
            <Button
              data-testid="confirm-modal-action"
              variant={confirmVariant}
              className="rounded-lg laptop:w-auto"
              onClick={handleConfirm}
              disabled={isConfirmLoading}
              size={isLaptopOrBigger ? 'regular' : 'large'}
              fullWidth
            >
              <TransText i18nKey={confirmLabel} />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmModal;
